import { useCallback, useEffect, useState } from 'react';
import { Form } from 'components';
import { useAttachmentFileBulkCreateMutation, useTaskCreateMutation } from 'api';
import moment from 'moment/moment';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { useLocation, useNavigate } from 'react-router-dom';
import { ADMIN_ROUTES } from 'constants/routes';

export const useAdd = () => {
  const [attachmentType, setAttachmentType] = useState<string | undefined>();
  const [fileList, setFileList] = useState<any[]>([]);
  const [attachments, setAttachments] = useState<{ type: string; file: any }[]>([]);
  const [form] = Form.useForm();
  const [disableSaveBtn, setDisableSaveBtn] = useState(true);
  const [description, setDescription] = useState('');
  const location = useLocation();
  const [createTask, { isLoading: createIsLoading }] = useTaskCreateMutation();
  const navigate = useNavigate();
  const { handleError, handleSuccess } = useNotificationMessages();
  const [createBulkFiles] = useAttachmentFileBulkCreateMutation();
  const { entityName, entityId } = location.state || ({} as { entityName?: string; entityId?: string });

  const onCancel = useCallback(() => {
    form.resetFields();
    history.back();
  }, [form]);

  const handleNavigate = useCallback(() => {
    switch (entityName) {
      case 'CLIENT':
        navigate(`${ADMIN_ROUTES.client}/${entityId}/tasks`);
        break;
      case 'BRAND':
        navigate(`${ADMIN_ROUTES.brand.view}/${entityId}/tasks`);
        break;
      case 'SUPPLIER':
        navigate(`${ADMIN_ROUTES.suppliers.view}/${entityId}/tasks`);
        break;
      case 'COUNTRY':
        navigate(`${ADMIN_ROUTES.country.view}/${entityId}/tasks`);
        break;
      case 'ITEM':
        navigate(`${ADMIN_ROUTES.items.view}/${entityId}/tasks`);
        break;
      case 'LEAD':
        navigate(`${ADMIN_ROUTES.leads.view}/${entityId}/tasks`);
        break;
      case 'DEAL':
        navigate(`${ADMIN_ROUTES.deals.view}/${entityId}/tasks`);
        break;
      case 'PURCHASE_REQUEST':
        navigate(`${ADMIN_ROUTES.purchaseRequest.view}/${entityId}/tasks`);
        break;
      case 'PURCHASE_ORDER':
        navigate(`${ADMIN_ROUTES.purchaseOrder.view}/${entityId}/tasks`);
        break;
      case 'ORDER':
        navigate(`${ADMIN_ROUTES.orders.view}/${entityId}/tasks`);
        break;
      default:
        navigate(ADMIN_ROUTES.tasks.list);
    }
  }, [navigate, entityId, entityName]);

  const onFinish = async (values: any) => {
    const payload = {
      ...values,
      file: undefined,
      dueDate: values.dueDate ? moment(values.dueDate.toISOString()).format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]') : '',
      description: description,
      attachmentTypes: attachments.map((i) => i.type),
    };

    if (entityName && entityId) {
      payload.entityType = entityName;
      payload.entityId = entityId;
    }

    try {
      const { data, error }: any = await createTask(payload);
      if (data?.success) {
        const formData = new FormData();
        if (attachments?.length && data?.data?.length) {
          formData.append('attachmentIds', data.data as any);
          Array.from(attachments).forEach((attachment) => {
            formData.append('files', attachment.file); // backend must handle 'files' as array
          });
          formData.append('serviceName', 'WRD');
          createBulkFiles({ data: formData, entityName: 'TASK' });
          handleSuccess('The task details were successfully added');
          handleNavigate();
        } else {
          handleSuccess('The task details were successfully added');
          handleNavigate();
        }
      } else {
        handleError(error);
      }
    } catch (err) {
      handleError(err);
    }
  };

  useEffect(() => {
    if (attachmentType) setDisableSaveBtn(false);
  }, [attachmentType]);

  const handleFormChange = useCallback(
    async (values: Record<string, any>) => {
      setDisableSaveBtn(false);
      if (values.entityType) {
        form.setFieldValue('entityId', undefined);
      }
    },
    [form]
  );

  return {
    onFinish,
    onCancel,
    attachments,
    setAttachments,
    form,
    disableSaveBtn,
    setDisableSaveBtn,
    description,
    setDescription,
    fileList,
    setFileList,
    attachmentType,
    setAttachmentType,
    createIsLoading,
    handleFormChange,
    entityName,
  };
};
